var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useCallback, useEffect, useMemo, useRef, } from 'react';
import { range, noop, isUndefined } from 'lodash-es';
import styled, { css } from 'styled-components';
import MultiRangeCalendar from './Calendar/MultiRangeCalendar';
import DateRangeInput from './Input/DateRangeInput';
import { ANCHOR_FROM, ANCHOR_TO } from './constants';
const Container = styled.div `
  ${({ alignItems }) => alignItems
    ? css `
          display: flex;
          flex-direction: column;
          align-items: ${alignItems};
        `
    : ''}
`;
const CalendarContainer = styled.div `
  ${({ block, alignItems }) => block
    ? css `
          display: flex;
          justify-content: ${alignItems};
        `
    : ''}
  display: ${({ open, block }) => {
    if (open && block)
        return 'flex';
    if (open && !block)
        return 'block';
    return 'none';
}};
`;
const DateRangeInputContainer = styled.div `
  ${({ isLast }) => !isLast
    ? css `
          margin-bottom: 10px;
        `
    : ''}
  ${({ block }) => block
    ? css `
          align-self: stretch;
        `
    : ''}
`;
export const ACTION_TYPE = {
    changeFromDate: 'changeFromDate',
    changeToDate: 'changeToDate',
    changeInputValueFrom: 'changeInputValueFrom',
    changeInputValueTo: 'changeInputValueTo',
    clickOutside: 'clickOutside',
    changeIsFocusOnToInput: 'changeIsFocusOnToInput',
    handleInputFromFocus: 'handleInputFromFocus',
    handleInputFromBlur: 'handleInputFromBlur',
    handleInputToFocus: 'handleInputToFocus',
    handleInputToBlur: 'handleInputToBlur',
    handleInputFromClickEnter: 'handleInputFromClickEnter',
    handleInputToClickEnter: 'handleInputToClickEnter',
};
const DateMultiRangePicker = ({ lang = 'en', rangeConfigs: rawRangeConfigs, disabled, disabledDate, state, dispatch, getDateColor, block = false, singleMonthMode, alignItems = 'flex-start', }) => {
    const containerRef = useRef(null);
    const rangeConfigs = useMemo(() => rawRangeConfigs ||
        range(state.ranges.length).map(index => ({
            key: `DateRangeInput_${index}`,
            fromInputRef: React.createRef(),
            toInputRef: React.createRef(),
        })), [rawRangeConfigs, state.ranges]);
    const [internalFocusedInput, setInternalFocusedInput] = useState(null);
    const isPropFocusedInputUndefined = isUndefined(state.focusedInput);
    const focusedInput = !isPropFocusedInputUndefined
        ? state.focusedInput
        : internalFocusedInput;
    const withInternalFocusedInputUpdate = useCallback((nextInternalFocusedInput, next = noop) => (e) => {
        if (isPropFocusedInputUndefined) {
            setInternalFocusedInput(nextInternalFocusedInput);
        }
        next(e);
    }, [isPropFocusedInputUndefined]);
    const generateHandleInputChange = useCallback((rangeIndex) => e => {
        var _a, _b, _c, _d;
        if (((_b = (_a = rangeConfigs[rangeIndex]) === null || _a === void 0 ? void 0 : _a.fromInputRef) === null || _b === void 0 ? void 0 : _b.current) === e.target) {
            dispatch.changeInputValueFrom({
                rangeIndex,
                value: e.target.value,
            });
        }
        if (((_d = (_c = rangeConfigs[rangeIndex]) === null || _c === void 0 ? void 0 : _c.toInputRef) === null || _d === void 0 ? void 0 : _d.current) === e.target) {
            dispatch.changeInputValueTo({ rangeIndex, value: e.target.value });
        }
    }, [dispatch, rangeConfigs]);
    const handleCalendarDateChange = useCallback((newDate) => {
        if (rangeConfigs && focusedInput) {
            const { anchor, rangeIndex } = focusedInput;
            switch (anchor) {
                case ANCHOR_FROM:
                    dispatch.changeFromDate({ rangeIndex, value: newDate });
                    break;
                case ANCHOR_TO:
                    dispatch.changeToDate({ rangeIndex, value: newDate });
                    break;
                default:
                    break;
            }
        }
    }, [focusedInput, dispatch, rangeConfigs]);
    useEffect(() => {
        if (!rangeConfigs)
            return noop;
        const handleOutsideClick = (e) => {
            // update new valid date when input blur (clicking outside)
            if (e.type === 'mousedown' &&
                e.target &&
                (!containerRef.current ||
                    containerRef.current.contains(e.target) ||
                    !e.target.isConnected)) {
                return;
            }
            if (dispatch.clickOutside)
                dispatch.clickOutside(e);
        };
        const handlerConfigs = rangeConfigs.reduce((result, { fromInputRef, toInputRef }, rangeIndex) => {
            const fromInput = fromInputRef === null || fromInputRef === void 0 ? void 0 : fromInputRef.current;
            const toInput = toInputRef === null || toInputRef === void 0 ? void 0 : toInputRef.current;
            if (fromInput) {
                result.push({
                    inputElement: fromInput,
                    type: 'from',
                    focusToNext: (e) => {
                        const keyCode = e.keyCode || e.which;
                        if ((dispatch === null || dispatch === void 0 ? void 0 : dispatch.handleInputFromClickEnter) && keyCode === 13) {
                            dispatch.handleInputFromClickEnter({ rangeIndex, e });
                        }
                    },
                });
            }
            if (toInput) {
                result.push({
                    inputElement: toInput,
                    type: 'to',
                    focusToNext: (e) => {
                        const keyCode = e.keyCode || e.which;
                        if ((dispatch === null || dispatch === void 0 ? void 0 : dispatch.handleInputToClickEnter) && keyCode === 13) {
                            dispatch.handleInputToClickEnter({ rangeIndex, e });
                        }
                    },
                });
            }
            return result;
        }, []);
        document.addEventListener('mousedown', handleOutsideClick);
        handlerConfigs.forEach((_a) => {
            var { inputElement, type } = _a, handler = __rest(_a, ["inputElement", "type"]);
            if (type === ANCHOR_FROM && handler.focusToNext) {
                inputElement.addEventListener('keyup', handler.focusToNext);
            }
            if (type === ANCHOR_TO && handler.focusToNext) {
                inputElement.addEventListener('keyup', handler.focusToNext);
            }
        });
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            handlerConfigs.forEach((_a) => {
                var { inputElement, type } = _a, handler = __rest(_a, ["inputElement", "type"]);
                if (type === ANCHOR_FROM && handler.focusToNext) {
                    inputElement.removeEventListener('keyup', handler.focusToNext);
                }
                if (type === ANCHOR_TO && handler.focusToNext) {
                    inputElement.removeEventListener('keyup', handler.focusToNext);
                }
            });
        };
    }, [dispatch, rangeConfigs]);
    useEffect(() => {
        // focus to another input won't get latest disabledDate
        // so we update a state to trigger Calendar component re-render
        const hasInputFromHandler = dispatch.handleInputFromFocus || dispatch.handleInputFromBlur;
        const hasInputToHandler = dispatch.handleInputToFocus || dispatch.handleInputToBlur;
        if (!rangeConfigs || (!hasInputFromHandler && !hasInputToHandler))
            return noop;
        const handlerConfigs = rangeConfigs.reduce((result, { fromInputRef, toInputRef }, rangeIndex) => {
            const fromInput = fromInputRef === null || fromInputRef === void 0 ? void 0 : fromInputRef.current;
            const toInput = toInputRef === null || toInputRef === void 0 ? void 0 : toInputRef.current;
            if (fromInput) {
                result.push({
                    inputElement: fromInput,
                    handleFocus: withInternalFocusedInputUpdate({ rangeIndex, anchor: ANCHOR_FROM }, e => { var _a; return (_a = dispatch.handleInputFromFocus) === null || _a === void 0 ? void 0 : _a.call(dispatch, { rangeIndex, e }); }),
                    handleBlur: withInternalFocusedInputUpdate(null, e => { var _a; return (_a = dispatch.handleInputFromBlur) === null || _a === void 0 ? void 0 : _a.call(dispatch, { rangeIndex, e }); }),
                });
            }
            if (toInput) {
                result.push({
                    inputElement: toInput,
                    handleFocus: withInternalFocusedInputUpdate({ rangeIndex, anchor: ANCHOR_TO }, e => { var _a; return (_a = dispatch.handleInputToFocus) === null || _a === void 0 ? void 0 : _a.call(dispatch, { rangeIndex, e }); }),
                    handleBlur: withInternalFocusedInputUpdate(null, e => { var _a; return (_a = dispatch.handleInputToBlur) === null || _a === void 0 ? void 0 : _a.call(dispatch, { rangeIndex, e }); }),
                });
            }
            return result;
        }, []);
        handlerConfigs.forEach(({ inputElement, handleBlur, handleFocus }) => {
            if (handleBlur) {
                inputElement.addEventListener('blur', handleBlur);
            }
            if (handleFocus) {
                inputElement.addEventListener('focus', handleFocus);
            }
        });
        return () => {
            handlerConfigs.forEach(({ inputElement, handleBlur, handleFocus }) => {
                if (handleBlur) {
                    inputElement.removeEventListener('blur', handleBlur);
                }
                if (handleFocus) {
                    inputElement.removeEventListener('focus', handleFocus);
                }
            });
        };
    }, [
        withInternalFocusedInputUpdate,
        dispatch.handleInputFromFocus,
        dispatch.handleInputFromBlur,
        dispatch.handleInputToBlur,
        dispatch.handleInputToFocus,
        rangeConfigs,
    ]);
    return (React.createElement(Container, { alignItems: alignItems, ref: containerRef },
        rangeConfigs.map(({ fromInputRef, toInputRef, title, focusColor, key }, rangeIndex) => {
            var _a, _b;
            return state.ranges[rangeIndex] && (React.createElement(DateRangeInputContainer, { key: key, isLast: rangeIndex === state.ranges.length, block: block },
                title,
                React.createElement(DateRangeInput, { disabled: disabled, block: block, lang: lang, valueFrom: (_a = state.ranges[rangeIndex]) === null || _a === void 0 ? void 0 : _a.inputFrom, valueTo: (_b = state.ranges[rangeIndex]) === null || _b === void 0 ? void 0 : _b.inputTo, fromRef: fromInputRef, toRef: toInputRef, onChange: generateHandleInputChange(rangeIndex), focusColor: focusColor })));
        }),
        React.createElement(CalendarContainer, { onMouseDown: e => e.preventDefault(), block: block, open: !disabled },
            React.createElement(MultiRangeCalendar, { lang: lang, open: !disabled, ranges: state.ranges, onChange: handleCalendarDateChange, disabledDate: disabledDate, getDateColor: getDateColor, singleMonthMode: singleMonthMode, focusedInput: focusedInput }))));
};
export default DateMultiRangePicker;
//# sourceMappingURL=DateMultiRangePicker.js.map